import type { Scene } from '@lumen5/luminary';

import { Tool } from '../tools/Tool';
import type { EnhancedToolResult, ToolDefinition, ToolExecutionContext } from '../types';

import { getHtmlDesignTarget } from 'js/src/data/html-design';
import { setSceneDurationUserOverride } from 'js/src/data/user-overrides';
import { setSceneDuration } from 'js/src/luminary/utils';
import { isVoicedrivenScene } from 'js/src/luminary/voicedriven-scenes';

interface SetSceneDurationArgs {
    durationMs: number;
}

/**
 * Sets the playback duration of the scene the active graphic lives on — text-on-media only, where the
 * duration is the designer's to size; a voice-driven scene is fixed by its voiceover, so the tool
 * refuses with a teaching error. Resolves the active scene like CaptureSceneTool and marks the new
 * duration a user override so a later text-driven recalc doesn't recompute it away.
 *
 * NOTE: the tool name `set_scene_duration` (and this class name) mirrors the general Toolbox
 * `agent/tools/SetSceneDurationTool`. They live in separate agents, so nothing collides today — but
 * if the html-design tools are ever folded into `ToolboxRootAgent.getTools()`, one of the two must be
 * renamed (e.g. `set_graphic_scene_duration`) to avoid two identically-named tools in one list.
 */
export class SetSceneDurationTool extends Tool<SetSceneDurationArgs> {
    public static readonly toolName = 'set_scene_duration';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: SetSceneDurationTool.toolName,
                description:
                    'Set how long this scene — and so your graphic — plays, in milliseconds. Only ' +
                    'works when the scene is text-on-media (no voiceover); on a voice-driven scene the ' +
                    'duration is fixed by the voiceover and this call is refused. Recommended flow: ' +
                    'draft the graphic first, estimate how long the content needs to read comfortably ' +
                    'and for the motion to breathe, set the duration to fit, then let the reading-time ' +
                    'lint confirm. Decide the duration early (from a first draft) and adjust if needed, ' +
                    'but before fine-tuning the timeline — the whole animation is authored against it.',
                parameters: {
                    type: 'object',
                    properties: {
                        durationMs: {
                            type: 'number',
                            description: 'Target scene duration in milliseconds.',
                        },
                    },
                    required: ['durationMs'],
                },
            },
        };
    }

    public getToolDisplayName(): string {
        return 'Setting scene duration';
    }

    public get statusIcon(): string {
        return 'timer';
    }

    public async execute(
        args: SetSceneDurationArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const scene = this.resolveScene(context.conversationId);
        if (!scene) {
            return {
                result: 'Error: could not find the active graphic’s scene to set the duration on.',
                isSuccess: false,
            };
        }

        if (isVoicedrivenScene(scene)) {
            return {
                result:
                    `This scene's duration is fixed at ${scene.duration}ms by its voiceover and ` +
                    "can't be changed. Design the graphic to fit that window.",
                isSuccess: false,
            };
        }

        // No range cap — the reading-time lint and agent judgment govern whether the value is sensible.
        // No batchRun: it's a single mutation, and the scene.duration setter already batches internally.
        setSceneDuration(this.luminary, scene.id, args.durationMs);

        // Mark it a user override so a later text-driven recommended-duration recalc doesn't silently
        // reset the value the agent sized the graphic against (mirrors the Toolbox set_scene_duration
        // tool). Undo/redo (checkpointVideo) is intentionally out of scope here — see LUM-555.
        this.dispatch(
            setSceneDurationUserOverride({
                sceneId: scene.id,
                isAutomatic: false,
                systemTriggered: false,
                parentActionGroup: context.parentActionGroup,
            }),
        );

        return {
            result: `Set the scene duration to ${args.durationMs}ms.`,
            isSuccess: true,
        };
    }

    private resolveScene(conversationId: string | undefined): Scene | undefined {
        const target = getHtmlDesignTarget(this.getState(), conversationId);
        return target ? this.luminary.getSceneById(target.sceneId) : undefined;
    }
}
